import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

// Short column headers to fit landscape A4 (10 attribute columns + meta).
// Hover-title / first row repeats the full SCA name for clarity.
const ATTRS = [
  { short: "Frag/Arom", full: "Fragrance / Aroma" },
  { short: "Flavour",   full: "Flavour" },
  { short: "Aftertst",  full: "Aftertaste" },
  { short: "Acidity",   full: "Acidity" },
  { short: "Body",      full: "Body" },
  { short: "Balance",   full: "Balance" },
  { short: "Sweet",     full: "Sweetness" },
  { short: "Clean",     full: "Clean cup" },
  { short: "Unif",      full: "Uniformity" },
  { short: "Overall",   full: "Overall" },
];

// Print-friendly blank scorecard — horizontal layout.
// Each sample is a row; attributes are columns. Cupper writes the score
// directly in the empty cell below each attribute header. One comment
// box per sample at the right edge. Landscape A4 fits 6–10 samples.
export default function ScorecardPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/cuppings/${id}`).then((r) => setData(r.data))
      .catch(() => setData({ name: "Cupping", samples: [] }));
  }, [id]);

  if (!data) return null;
  const samples = (data.samples || []).length > 0
    ? data.samples
    : [{ sample_id: "blank", label: "Sample A" }];

  return (
    <div className="scorecard-root" style={{ background: "white", color: "black", padding: "20px", minHeight: "100vh", fontFamily: "Manrope, sans-serif" }}>
      <style>{`
        @page { size: A4 landscape; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .scorecard-root { padding: 0 !important; }
        }
        table.sc {
          border-collapse: collapse;
          width: 100%;
          table-layout: fixed;
        }
        table.sc th, table.sc td {
          border: 1px solid #555;
          padding: 4px 5px;
          font-size: 10.5px;
          vertical-align: middle;
        }
        table.sc thead th {
          background: #f0ece4;
          text-align: center;
          font-weight: 600;
        }
        table.sc th.attr {
          font-size: 9.5px;
          letter-spacing: 0.02em;
          line-height: 1.15;
        }
        table.sc td.label-cell {
          text-align: left;
          font-size: 10px;
          line-height: 1.2;
        }
        table.sc td.score-cell {
          height: 48px;
          text-align: center;
        }
        table.sc td.total-cell {
          background: #fdf6f0;
          font-weight: 600;
          text-align: center;
        }
        table.sc td.comment-cell {
          text-align: left;
          vertical-align: top;
          font-size: 9.5px;
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: 14, display: "flex", gap: 12 }}>
        <button onClick={() => window.print()}
          data-testid="print-scorecard"
          style={{ padding: "8px 16px", background: "#8C583E", color: "white", border: "none", borderRadius: 4, fontWeight: 600, cursor: "pointer" }}>
          Print / Save as PDF
        </button>
        <button onClick={() => window.close()}
          style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
          Close
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12, borderBottom: "2px solid #1C1816", paddingBottom: 6 }}>
        <div>
          <p style={{ fontSize: 9.5, letterSpacing: 1.5, textTransform: "uppercase", margin: 0, color: "#736B63" }}>
            Brothers Coffee Roastery — Cupping Scorecard
          </p>
          <h1 style={{ margin: "3px 0 0 0", fontSize: 19 }}>{data.name}</h1>
        </div>
        <div style={{ textAlign: "right", fontSize: 10.5 }}>
          <p style={{ margin: 0 }}><strong>Date:</strong> {data.cupping_date}</p>
          <p style={{ margin: 0 }}><strong>Cupper:</strong> _______________________</p>
        </div>
      </div>

      <p style={{ fontSize: 10, color: "#736B63", marginBottom: 8 }}>
        Score each attribute 0–10 (0.25 increments). Defects subtract from the
        sum to give the Total. Photograph the page and upload via "Scan paper
        scorecard" — AI will populate the digital scores.
      </p>

      <table className="sc">
        <colgroup>
          <col style={{ width: "10%" }} />
          {ATTRS.map((_, i) => <col key={i} style={{ width: `${56 / ATTRS.length}%` }} />)}
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "24%" }} />
        </colgroup>
        <thead>
          <tr>
            <th rowSpan={2} style={{ textAlign: "left", paddingLeft: 6 }}>Sample</th>
            <th colSpan={ATTRS.length}>Attribute scores (0–10)</th>
            <th rowSpan={2}>Def</th>
            <th rowSpan={2}>Total</th>
            <th rowSpan={2} style={{ textAlign: "left", paddingLeft: 6 }}>Comment</th>
          </tr>
          <tr>
            {ATTRS.map((a) => (
              <th key={a.short} className="attr" title={a.full}>{a.short}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => (
            <tr key={s.sample_id}>
              <td className="label-cell">
                <strong>{String.fromCharCode(65 + i)}</strong>
                <div style={{ fontSize: 9, color: "#555" }}>{s.label}</div>
                {(s.roast_number || s.roast_origin_name) && (
                  <div style={{ fontSize: 8.5, color: "#777" }}>
                    {s.roast_number ? `#${s.roast_number}` : ""}
                    {s.roast_number && s.roast_origin_name ? " · " : ""}
                    {s.roast_origin_name || ""}
                  </div>
                )}
              </td>
              {ATTRS.map((a) => (
                <td key={a.short} className="score-cell">&nbsp;</td>
              ))}
              <td className="score-cell">&nbsp;</td>
              <td className="total-cell">&nbsp;</td>
              <td className="comment-cell">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
